
import Vue from 'vue'
import ProjectList from '@/components/project/ProjectList.vue'
import PagedResults from '@/service/PagedResults'
import { ProjectListFilters } from '@/model/ProjectListFilters'
import { Category } from '@/model/Category'
import { Contest } from '@/model/Contest'
import { Tag } from '@/model/Tag'

export default Vue.extend({
  name: 'Projects',
  components: {
    ProjectList,
  },
  props: {
    category: {
      type: String,
      default: 'all',
    },
    contest: {
      type: String,
      default: 'any-contest',
    },
    filter: {
      type: String,
      default: 'all',
    },
    sort: {
      type: String,
      default: 'newest',
    },
  },

  created() {
    this.$watch(
      () => this.$route.params,
      (toParams) => {
        const isEmptyParams = Object.values(toParams).every(param => param === undefined)

        if (isEmptyParams) {
          this.selectedCategory = {} as Category
          this.currentCategory = {} as Category
          this.selectedContest = {} as Contest
          this.currentContest = {} as Contest
          this.selectedTags = [] as string[]
          this.currentTags = [] as string[]

          this.setCurrentFilters()
        }
      }
    )
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$route.query.action == 'add-new-project') {
        this.addNewProjectClick()
      }
    })

    const apiPromises = []
    const allCategories = new PagedResults<Category[]>(this.$api)
    const allContests = new PagedResults<Contest[]>(this.$api)
    const allTags = new PagedResults<Tag[]>(this.$api)

    // 'Sort by' select filter
    this.currentSort = this.sort

    // Api promise for 'Filter by category' select filter
    apiPromises.push(
      allCategories
        .all('/categories', 30)
        .then((response) => (this.categories = response))
    )

    apiPromises.push(
      allContests
        .all('/contests/filters', 30)
        .then((response) => (this.contests = response))
    )

    apiPromises.push(
      allTags
        .all('/tags/filters', 30)
        .then((response) => (this.tags = response))
    )

    Promise.allSettled(apiPromises)
      .then(() => {
        // 'Filter by category' select filter
        if (this.category !== 'all') {
          this.currentCategory = this.categories.find((category) => (category.slug === this.category))
          this.selectedCategory = this.currentCategory
        }

        // 'Filter by contests' select filter
        if (this.contest !== 'any-contest') {
          this.currentContest = this.contests.find((contest) => (contest.slug === this.contest))
          this.selectedContest = this.currentContest
        }

        // 'Filter by tag' multiselect filter
        if (this.filter !== 'all') {
          this.currentTags = this.filter.split(',')
          this.selectedTags = this.filter.split(',')
        }
      })
      .catch(() =>
        this.$store.dispatch(
          'alert/error',
          'Problem with reading API for filters.'
        )
      )
      .finally(()=> {
        // Set current filters for ProjectList
        this.setCurrentFilters()
      });
  },

  data: () => ({
    sortOrderItems: [
      {
        text: 'Newest first',
        value: 'newest',
      },
      {
        text: 'Oldest first',
        value: 'oldest',
      },
    ],
    currentSort: '',
    currentFilters: {} as ProjectListFilters,
    currentCategory: {} as Category | undefined,
    selectedCategory: {} as Category | undefined,
    categories: [] as Category[],
    currentContest: {} as Contest | undefined,
    selectedContest: {} as Contest | undefined,
    contests: [] as Contest[],
    currentTags: [] as string[],
    selectedTags: [] as string[],
    tags: [] as Tag[],
  }),

  methods: {
    addNewProjectClick: function () {
      if (!this.$store.state.authentication.signedIn) {
        this.$store.dispatch(
          'alert/warning',
          'Please sign in to add new project.'
        )
        this.$router.push(`/sign-in?returnTo=${this.$route.path}?action=add-new-project`)
      }
      if (this.$store.state.authentication.user.status === 'waiting_for_parent') {
        this.$store.dispatch(
          'alert/warning',
          "We are waiting for your parent's consent so you can't add new projects."
        )
        return
      }
      this.$router.push('/project/new')
    },
    setCurrentFilters: function (): ProjectListFilters {
      return this.currentFilters = {'tags': this.currentTags, 'category': this.currentCategory, 'contest': this.currentContest}
    },
    filterClick: function () {
      this.currentTags = [...this.selectedTags]
      this.currentCategory = this.selectedCategory
      this.currentContest = this.selectedContest
      this.setCurrentFilters()
      this.pushNewRouterState()
    },
    pushNewRouterState: function () {
      const newSort = this.currentSort
      const newCategory = this.currentCategory?.slug || 'all'
      const newContest = this.currentContest?.slug || 'any-contest'
      const newFilter = this.currentTags.length > 0 ? this.currentTags.join(',') : 'all'

      if (this.sort === newSort &&
          this.category === newCategory &&
          this.contest === newContest &&
          this.filter === newFilter
      ) {
        return
      }

      this.$router.push({
        name: 'Projects',
        params: {
          sort: newSort,
          category: newCategory,
          contest: newContest,
          filter: newFilter,
        },
      })
    },
  },
})
